import { EDITOR_PPM as PPM } from './floorPlan.logic.ts';
import { formatRackName } from './rackName.logic.ts';
import { facadeFromApiRotation } from './draftRotation.logic.ts';
import { isFacilityType, isArchitectureType } from './floorEntities.logic.ts';
import { normalizeRackStatus, normalizeRackRole } from './rackLifecycle.logic.ts';

const CELL_PX = 60;

export function mapApiRack(
  rack: any,
  isDraft = false,
  defaultSettings?: { defaultRackWidthM?: number; defaultRackDepthM?: number; defaultRackHeightM?: number }
): any {
  const id = isDraft && rack.row_index >= 10000 
    ? `dup-${rack.row_index}-${rack.rack_index}` 
    : `rack-${rack.row_index}-${rack.rack_index}`;
  const facade = facadeFromApiRotation(rack.rotation);
  const rackType = rack.type || 'server_rack';
  const isFac = rack.is_facility != null ? Boolean(rack.is_facility) : isFacilityType(rackType);
  const isBlk = rack.is_blocked != null ? Boolean(rack.is_blocked) : isArchitectureType(rackType);
  const uSlots = rack.u_slots != null ? rack.u_slots : (isFac ? 0 : 42);

  return {
    id,
    x: rack.x_m != null ? Math.round(rack.x_m * PPM) : (rack.rack_index ?? 0) * CELL_PX,
    y: rack.y_m != null ? Math.round(rack.y_m * PPM) : (rack.row_index ?? 0) * CELL_PX,
    width: Math.round((rack.width_m || 0.6) * PPM),
    height: Math.round((rack.depth_m || 1.0) * PPM),
    ...facade,
    label: rack.label || formatRackName(undefined, undefined, rack.row_index ?? 0, rack.rack_index ?? 0),
    rackType,
    uCount: uSlots,
    isFacility: isFac,
    isBlocked: isBlk,
    widthM: rack.width_m || defaultSettings?.defaultRackWidthM || (isBlk ? 0.6 : 0.6),
    depthM: rack.depth_m || defaultSettings?.defaultRackDepthM || (isBlk ? 0.6 : 1.0),
    heightM: rack.height_m || defaultSettings?.defaultRackHeightM || 2.0,
    tenant: rack.tenant || 'mine',
    status: normalizeRackStatus(rack.status),
    role: normalizeRackRole(rack.role, rackType),
    rowId: rack.row_id,
    rackPosInRow: rack.rack_pos_in_row,
    isDirty: false,
    rowIndex: rack.row_index ?? 0,
    rackIndex: rack.rack_index ?? 0,
    dbId: rack.id,
    max_power_w: rack.max_power_w,
  };
}

export function buildStagedChangesJson(racksRecord: Record<string, any>, baselineRacks: Record<string, any>, rows: Record<string, any>): any {
  const diff: any = { updated: {}, added: [], deleted: [] };
  const currentDbIds = new Set<number>();

  Object.values(racksRecord).forEach(r => {
    let ri = r.rowIndex ?? 0;
    let rk = r.rackIndex ?? 0;
    if (r.rowId && rows[r.rowId]) {
      ri = rows[r.rowId].labelIndex;
      rk = r.rackPosInRow || r.rackIndex;
    }

    const isFac = r.isFacility != null ? Boolean(r.isFacility) : isFacilityType(r.rackType);
    const isBlk = r.isBlocked != null ? Boolean(r.isBlocked) : isArchitectureType(r.rackType);

    const payload = {
      row_index: ri,
      rack_index: rk,
      x_m: +(r.x / PPM).toFixed(3),
      y_m: +(r.y / PPM).toFixed(3),
      rotation: r.rotation ?? 0,
      type: r.rackType,
      u_slots: isFac ? 0 : (r.uCount ?? 42),
      is_facility: isFac,
      is_blocked: isBlk,
      label: r.label,
      width_m: r.widthM,
      depth_m: r.depthM,
      height_m: r.heightM,
      tenant: r.tenant,
      status: r.status || 'active',
      role: r.role || normalizeRackRole(r.role, r.rackType),
      row_id: r.rowId,
      rack_pos_in_row: r.rackPosInRow,
      max_power_w: r.max_power_w,
    };

    if (!r.dbId) {
      diff.added.push(payload);
    } else {
      currentDbIds.add(r.dbId);
      const baseline = Object.values(baselineRacks).find(b => b.dbId === r.dbId);
      if (baseline) {
        const isChanged = (
           r.x !== baseline.x || r.y !== baseline.y || r.rotation !== baseline.rotation ||
           r.rowId !== baseline.rowId || r.rackPosInRow !== baseline.rackPosInRow ||
           r.tenant !== baseline.tenant || r.rackType !== baseline.rackType ||
           r.label !== baseline.label || r.widthM !== baseline.widthM ||
           r.depthM !== baseline.depthM || r.heightM !== baseline.heightM ||
           r.uCount !== baseline.uCount || r.max_power_w !== baseline.max_power_w ||
           r.status !== baseline.status || r.role !== baseline.role
        );
        if (isChanged || r.isDirty) {
           diff.updated[r.dbId.toString()] = payload;
        }
      } else {
        diff.updated[r.dbId.toString()] = payload;
      }
    }
  });

  Object.values(baselineRacks).forEach(b => {
    if (b.dbId && !currentDbIds.has(b.dbId)) {
      diff.deleted.push(b.dbId);
    }
  });

  return diff;
}
